import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import type { RdScheduleWeekResponse } from "../../../shared/rd-schedule.js";
import { authenticatedApi } from "./api.js";
import { weekMirrorPoll } from "./mirror-state.js";
import {
  rdScheduleWeekApiPath,
  rdScheduleWeekQueryKey,
  type RdScheduleWeekRequest,
} from "./rd-schedule-week.js";

/** Fresh for a short while so flipping filters or the key never refetches. */
export const RD_SCHEDULE_WEEK_STALE_MS = 15_000;

/**
 * NO-874: one RD's week (GET /api/rd-schedule/week). Enabled only while the
 * Week view is on screen. A neighbouring week keeps the previous answer on
 * screen, dimmed, within one selection; a different RD never inherits it.
 */
export function useRdScheduleWeek(request: RdScheduleWeekRequest, enabled: boolean) {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: rdScheduleWeekQueryKey(request),
    queryFn: () =>
      authenticatedApi<{ data: RdScheduleWeekResponse }>(
        getToken,
        rdScheduleWeekApiPath(request)
      ),
    enabled,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[3] === (request.rdId ?? null) ? previousData : undefined,
    staleTime: RD_SCHEDULE_WEEK_STALE_MS,
    gcTime: 0,
    // NO-917: a visit still waiting for its Healthie copy reads again.
    refetchInterval: (query) => weekMirrorPoll(query.state.data),
  });
}
